import type { PartnerNoti } from '@seek/indie-api-types';
import {
  Button,
  Column,
  Columns,
  Dialog,
  IconDelete,
  IconEdit,
  Inline,
  Loader,
  Text,
  TextLinkButton,
} from 'braid-design-system';
import { useState } from 'react';

import type { Item } from '../../MultiItemConfig.tsx';

import { ItemDialog } from './ItemDialog.tsx';

interface ItemRowProps {
  item: Item;
  removeItem: (itemToRemove: Item) => Promise<void>;
  itemKey: 'emailAddresses' | 'phoneNumbers';
  notiConfig: PartnerNoti.PartnerNotiConfig;
}

export const ItemRow = ({
  item,
  removeItem,
  itemKey,
  notiConfig,
}: ItemRowProps) => {
  const [loading, setLoading] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showUpdateDialog, setShowUpdateDialog] = useState(false);

  const deleteOnClick = () => {
    setShowConfirmDialog(true);
  };

  const confirmDelete = async () => {
    if (loading) return;

    setLoading(true);
    await removeItem(item);
    setLoading(false);
    setShowConfirmDialog(false);
  };

  const cancelDelete = () => {
    setShowConfirmDialog(false);
  };

  const updateOnClick = () => {
    setShowUpdateDialog(true);
  };

  return (
    <>
      <Columns space="large" alignY="bottom">
        <Column>
          <Text>{'email' in item ? item.email : item.number}</Text>
        </Column>

        <Column width="content">
          <Inline space="small">
            <Text tone="secondary">
              <TextLinkButton onClick={updateOnClick}>
                {loading ? null : <IconEdit />}
              </TextLinkButton>
            </Text>
            <Text tone="critical">
              <TextLinkButton onClick={deleteOnClick}>
                {loading ? <Loader size="xsmall" /> : <IconDelete />}
              </TextLinkButton>
            </Text>
          </Inline>
        </Column>
      </Columns>

      {showUpdateDialog && (
        <ItemDialog
          action="update"
          selectedItem={item}
          newFormTitle="Update notification settings"
          setShowItemDialog={setShowUpdateDialog}
          showItemDialog={showUpdateDialog}
          itemKey={itemKey}
          notiConfig={notiConfig}
        />
      )}

      <Dialog
        open={showConfirmDialog}
        onClose={cancelDelete}
        id="confirmDelete"
        title={`Are you sure you want to delete this ${
          'email' in item ? 'email address' : 'phone number'
        }?`}
      >
        <Text>{'email' in item ? item.email : item.number}</Text>
        <Inline space="gutter">
          <Button onClick={confirmDelete} tone="neutral" variant="soft">
            Confirm
          </Button>
          <Button onClick={cancelDelete} tone="critical" variant="soft">
            Cancel
          </Button>
        </Inline>
      </Dialog>
    </>
  );
};
